/**
 * Shared utility functions used by the messages panel, notifications panel,
 * and utility bar components for date formatting and API request handling.
 */

export function formatDate(value: string, includeTime = true, timeZone?: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' } : {}),
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function toDateTimeLocal(
  value: string,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

export function toIso(
  value: string,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const [datePart = '', timePart = ''] = value.split('T');
  const [year = 1970, month = 1, day = 1] = datePart.split('-').map(Number);
  const [hour = 0, minute = 0] = timePart.split(':').map(Number);
  const desired = Date.UTC(year, month - 1, day, hour, minute);
  let candidate = desired;
  for (let iteration = 0; iteration < 3; iteration += 1) {
    const rendered = toDateTimeLocal(new Date(candidate).toISOString(), timeZone);
    const [renderedDate = '', renderedTime = ''] = rendered.split('T');
    const [renderedYear = 1970, renderedMonth = 1, renderedDay = 1] = renderedDate
      .split('-')
      .map(Number);
    const [renderedHour = 0, renderedMinute = 0] = renderedTime.split(':').map(Number);
    const renderedUtc = Date.UTC(
      renderedYear,
      renderedMonth - 1,
      renderedDay,
      renderedHour,
      renderedMinute,
    );
    candidate += desired - renderedUtc;
  }
  return new Date(candidate).toISOString();
}

export async function readApiError(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { message?: string; error?: string };
    return payload.message ?? payload.error ?? `Request failed (HTTP ${response.status}).`;
  } catch {
    return `Request failed (HTTP ${response.status}).`;
  }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export function supersededRequestError(): Error {
  const error = new Error('Request superseded by an account or organisation change.');
  error.name = 'AbortError';
  return error;
}
